import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  List,
  ListItem
} from '@mui/material';

import { Edit, Trash2, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import { getData, postData, putData, deleteData } from '../../Webservice/Webservice';

const Settings = () => {
  const [menus, setMenus] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedMenuFor, setSelectedMenuFor] = useState('admin');
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    parentId: '',
    menuFor: 'admin',
    status: 'active',
    icon: 'MdDashboard',
    assignToRoles: []
  });

  const fetchMenus = async () => {
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const response = await getData(`menu/list?menuFor=${selectedMenuFor}`, null, `Bearer ${token}`);
      if (Array.isArray(response)) {
        setMenus(response);
      } else {
        setMenus([]);
      }
    } catch (error) {
      console.error('Error fetching menus:', error);
      toast.error('Failed to load menus');
    }
  };

  const fetchRoles = async () => {
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const response = await getData('role/list', null, `Bearer ${token}`);
      if (response && response.roles) setRoles(response.roles);
      else if (Array.isArray(response)) setRoles(response);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  useEffect(() => {
    fetchMenus();
    fetchRoles();
  }, [selectedMenuFor]);

  const handleOpen = (menuForEdit = null) => {
    if (menuForEdit && menuForEdit.name) {
      // Edit context
      setIsEditing(true);

      // Determine which roles currently have this menu
      const currentRolesWithMenu = roles
        .filter(role => role.menus && role.menus.some(m => (m._id || m).toString() === menuForEdit._id.toString()))
        .map(role => role._id);

      setFormData({
        id: menuForEdit._id,
        name: menuForEdit.name,
        parentId: menuForEdit.parentId || '',
        menuFor: menuForEdit.menuFor || selectedMenuFor,
        status: menuForEdit.status || 'active',
        icon: menuForEdit.icon || 'MdDashboard',
        assignToRoles: currentRolesWithMenu
      });
    } else if (menuForEdit && menuForEdit.parentId) {
      // Add Submenu context (via Quick Add Submenu)
      setIsEditing(false);
      setFormData({
        id: null,
        name: '',
        parentId: menuForEdit.parentId,
        menuFor: menuForEdit.menuFor || selectedMenuFor,
        status: 'active',
        icon: 'MdDashboard',
        assignToRoles: []
      });
    } else {
      // New Main Menu Context
      setIsEditing(false);
      setFormData({
        id: null,
        name: '',
        parentId: '',
        menuFor: selectedMenuFor,
        status: 'active',
        icon: 'MdDashboard',
        assignToRoles: []
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Menu name cannot be empty');
      return;
    }

    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const payload = {
        name: formData.name,
        menuFor: formData.menuFor,
        parentId: formData.parentId || null,
        status: formData.status,
        icon: formData.icon,
        assignToRoles: formData.assignToRoles
      };

      if (isEditing) {
        await putData(`menu/update/${formData.id}`, payload, `Bearer ${token}`);
        toast.success('Menu updated successfully');
      } else {
        await postData('menu/add', payload, `Bearer ${token}`);
        toast.success('Menu created successfully');
      }
      handleClose();
      fetchMenus();
    } catch (error) {
      console.error('Error saving menu:', error);
      toast.error('Failed to save menu');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this menu? Submenus will also be deleted.')) {
      try {
        const token = sessionStorage.getItem('token') || localStorage.getItem('token');
        await deleteData(`menu/delete/${id}`, `Bearer ${token}`);
        toast.success('Menu deleted successfully');
        fetchMenus();
      } catch (error) {
        toast.error('Failed to delete menu');
      }
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 1200, margin: '0 auto' }}>
      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Menu Configuration Settings
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus />}
          onClick={() => handleOpen()}
          sx={{ bgcolor: 'rgb(255 209 2)', color: 'black', '&:hover': { bgcolor: '#e6bd00' } }}
        >
          Add Menu
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <FormControl variant="outlined" sx={{ minWidth: 200 }}>
          <InputLabel>Menu Target</InputLabel>
          <Select
            value={selectedMenuFor}
            onChange={(e) => setSelectedMenuFor(e.target.value)}
            label="Menu Target"
          >
            <MenuItem value="admin">Admin Panel</MenuItem>
            <MenuItem value="warehouse">Warehouse Panel</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {menus.map((menu) => (
              <React.Fragment key={menu._id}>
                <TableRow>
                  <TableCell>{menu.name}</TableCell>
                  <TableCell>Main Menu</TableCell>
                  <TableCell>{menu.status}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="text" color="primary" onClick={() => handleOpen({ parentId: menu._id, menuFor: menu.menuFor })} startIcon={<Plus size={16} />}>
                      Add Submenu
                    </Button>
                    <IconButton onClick={() => handleOpen(menu)} size="small" color="primary">
                      <Edit size={18} />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(menu._id)} size="small" color="error">
                      <Trash2 size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
                {menu.submenus && menu.submenus.map((submenu) => (
                  <TableRow key={submenu._id} sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ pl: 5 }}>-- {submenu.name}</TableCell>
                    <TableCell>Submenu</TableCell>
                    <TableCell>{submenu.status}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleOpen(submenu)} size="small" color="primary">
                        <Edit size={18} />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(submenu._id)} size="small" color="error">
                        <Trash2 size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
            {menus.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">No menus found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Menu' : 'Add New Menu'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Menu For</InputLabel>
              <Select
                value={formData.menuFor}
                label="Menu For"
                onChange={(e) => setFormData({ ...formData, menuFor: e.target.value })}
              >
                <MenuItem value="admin">Admin Panel</MenuItem>
                <MenuItem value="warehouse">Warehouse Panel</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Menu Name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />

            <FormControl fullWidth>
              <InputLabel>Menu Icon (Material Icon Name)</InputLabel>
              <Select
                value={formData.icon}
                label="Menu Icon (Material Icon Name)"
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              >
                <MenuItem value="MdDashboard">Dashboard</MenuItem>
                <MenuItem value="MdPerson">Users/Profile</MenuItem>
                <MenuItem value="MdStore">Store/Supplier</MenuItem>
                <MenuItem value="MdBusiness">Seller/Business</MenuItem>
                <MenuItem value="MdInventory2">Inventory</MenuItem>
                <MenuItem value="MdListAlt">List/Catalog</MenuItem>
                <MenuItem value="MdShoppingCart">Orders/Sales</MenuItem>
                <MenuItem value="MdAssessment">Reports/Analytics</MenuItem>
                <MenuItem value="MdAdminPanelSettings">Admin/Roles</MenuItem>
                <MenuItem value="MdLocalShipping">Shipping/Logistics</MenuItem>
                <MenuItem value="MdPlaylistAddCheck">Tasks/Checks</MenuItem>
                <MenuItem value="MdAllInbox">Packing/Inbox</MenuItem>
                <MenuItem value="MdAssignmentReturn">Returns</MenuItem>
                <MenuItem value="MdSettings">Settings</MenuItem>
                <MenuItem value="MdMenu">Menu Config</MenuItem>
                <MenuItem value="MdCancel">Cancel/Expired</MenuItem>
                <MenuItem value="MdAccountCircle">Account</MenuItem>
                <MenuItem value="MdNotifications">Notifications</MenuItem>
                <MenuItem value="MdAttachMoney">Payout/Finance</MenuItem>
                <MenuItem value="MdLayers">Categories/Layers</MenuItem>
                <MenuItem value="MdDevices">Mobile Content</MenuItem>
                <MenuItem value="MdDescription">Policy/Terms</MenuItem>
                <MenuItem value="MdHelp">FAQs/Support</MenuItem>
                <MenuItem value="MdEventNote">Activity Logs</MenuItem>
                <MenuItem value="MdSecurity">Security/Login Log</MenuItem>
                <MenuItem value="MdRedeem">Rewards/Coupons</MenuItem>
                <MenuItem value="MdCampaign">Campaign/Marketing</MenuItem>
                <MenuItem value="MdSms">SMS</MenuItem>
                <MenuItem value="MdEmail">Email</MenuItem>
                <MenuItem value="MdMessage">Whatsapp</MenuItem>
                <MenuItem value="MdPeople">HR/Employee</MenuItem>
                <MenuItem value="MdWork">Work/Payroll</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Parent Menu (Optional)</InputLabel>
              <Select
                value={formData.parentId}
                label="Parent Menu (Optional)"
                onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
              >
                <MenuItem value=""><em>None (Main Menu)</em></MenuItem>
                {menus
                  .filter((m) => m._id !== formData.id) // Avoid self-parenting
                  .map((m) => (
                    <MenuItem key={m._id} value={m._id}>{m.name}</MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>Assign to Roles</Typography>
            <Paper variant="outlined" sx={{ p: 1, maxHeight: 200, overflowY: 'auto' }}>
              <List disablePadding>
                {roles.map((role) => (
                  <ListItem key={role._id} disablePadding>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={formData.assignToRoles?.includes(role._id) || false}
                          onChange={(e) => {
                            const currentRoles = formData.assignToRoles || [];
                            const newRoles = e.target.checked
                              ? [...currentRoles, role._id]
                              : currentRoles.filter(id => id !== role._id);
                            setFormData({ ...formData, assignToRoles: newRoles });
                          }}
                        />
                      }
                      label={<Typography variant="body2">{role.name.replace(/_/g, ' ')}</Typography>}
                      sx={{ ml: 1 }}
                    />
                  </ListItem>
                ))}
                {roles.length === 0 && <Typography variant="caption" color="textSecondary">Loading roles...</Typography>}
              </List>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ bgcolor: 'rgb(255 209 2)', color: 'black', '&:hover': { bgcolor: '#e6bd00' } }}>
            {isEditing ? 'Update Menu' : 'Save Menu'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
